/**
 * AnalyticsSummaryBlock — dashboard KPI row on post-publish Home.
 *
 * Reuses the real HealthDashboard KPI row so Home and Dashboard do not drift.
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Button, Grid } from '@material-ui/core';
import { BarChart } from '@material-ui/icons';
import HealthKpiRow from 'app/routes/search/analytic/HealthDashboard/HealthKpiRow';
import { ADMIN_RUNTIME_COMMANDS } from 'app/runtime/adminRuntime';
import { useShellAppContext } from 'features/shell/context/useShellAppContext';
import styles from './AnalyticsSummaryBlock.module.scss';

function emptyReport() {
  return {
    isFetching: false,
    error:      null,
    data:       [],
    queries:    { total: 0, filteredTotal: 0, otherTotal: 0, ctr: 0 },
    curve:      { data: [] },
  };
}

function normalizeReport(result) {
  if (!result || typeof result !== 'object') return emptyReport();
  return {
    ...emptyReport(),
    ...result,
    isFetching: false,
    error: null,
    queries: result.queries || emptyReport().queries,
    curve: result.curve || emptyReport().curve,
  };
}

function reportsAreEqual(left, right) {
  return left.error === right.error
    && left.isFetching === right.isFetching
    && left.queries?.total === right.queries?.total
    && left.queries?.ctr === right.queries?.ctr
    && (left.curve?.data?.length || 0) === (right.curve?.data?.length || 0)
    && (left.data?.length || 0) === (right.data?.length || 0);
}

function useAnalyticsReports(shopId, runtime) {
  const [reports, setReports] = useState(() => ({
    topQueries:  emptyReport(),
    zeroResults: emptyReport(),
  }));

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!runtime || !runtime.supports(ADMIN_RUNTIME_COMMANDS.ANALYTICS_REPORT)) return;

      const [topQueriesResult, zeroResultsResult] = await Promise.allSettled([
        runtime.sendMessageWithResponse(ADMIN_RUNTIME_COMMANDS.ANALYTICS_REPORT, {
          shopId, reportType: 'top_queries', limit: 50, timeframeDays: 30,
        }),
        runtime.sendMessageWithResponse(ADMIN_RUNTIME_COMMANDS.ANALYTICS_REPORT, {
          shopId, reportType: 'zero_results', limit: 50, timeframeDays: 30,
        }),
      ]);

      if (cancelled) return;

      const nextReports = {
        topQueries: normalizeReport(
          topQueriesResult.status === 'fulfilled' ? topQueriesResult.value : null,
        ),
        zeroResults: normalizeReport(
          zeroResultsResult.status === 'fulfilled' ? zeroResultsResult.value : null,
        ),
      };
      setReports((current) => (
        reportsAreEqual(current.topQueries, nextReports.topQueries)
        && reportsAreEqual(current.zeroResults, nextReports.zeroResults)
          ? current
          : nextReports
      ));
    }

    load().catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [runtime, shopId]);

  return reports;
}

export default function AnalyticsSummaryBlock({
  shopId,
  localizeMessage,
  analyticsRuntime,
}) {
  const history = useHistory();
  const { runtime: shellRuntime } = useShellAppContext();
  const reports = useAnalyticsReports(shopId, analyticsRuntime || shellRuntime);
  const dashboardPath = `/app/${shopId}/search/analytic/health`;

  return (
    <section className={styles.summary} data-testid="analytics-summary-block">
      <Grid container spacing={3}>
        <HealthKpiRow
          topQueries={reports.topQueries}
          zeroResults={reports.zeroResults}
          localizeMessage={localizeMessage}
          columns={2}
        />
        <Grid item xs={12}>
          <div className={styles.action}>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              startIcon={<BarChart />}
              onClick={() => history.push(dashboardPath)}
              disabled={!shopId}
              className={styles.dashboardButton}
              data-testid="analytics-dashboard-btn"
            >
              {localizeMessage('operator.home.analytics.open_dashboard')}
            </Button>
          </div>
        </Grid>
      </Grid>
    </section>
  );
}

AnalyticsSummaryBlock.propTypes = {
  shopId: PropTypes.string,
  localizeMessage: PropTypes.func.isRequired,
  analyticsRuntime: PropTypes.shape({
    supports: PropTypes.func,
    sendMessageWithResponse: PropTypes.func,
  }),
};

AnalyticsSummaryBlock.defaultProps = {
  shopId: null,
  analyticsRuntime: null,
};
